import { CATEGORICAL, CATEGORICAL_AUTO_SLOTS, CATEGORICAL_SLOT_COUNT, INK, NEUTRAL } from "../theme/tokens";
import { type Job, NurseFullRole, NurseFullRoleShort } from "./models";
import { hashString } from "./utilFunctions";

/** One categorical colour in its three shades — see `CATEGORICAL` in theme/tokens. */
export type CategoricalColor = {
	/** `null` for the neutral colour, which is no slot. */
	slot: number | null;
	dot: string;
	fill: string;
	ink: string;
};

/** Colour for things that have no categorical identity (an undefined role, a job outside the department). */
export const NEUTRAL_CATEGORICAL_COLOR: CategoricalColor = { slot: null, dot: NEUTRAL[450], fill: NEUTRAL[200], ink: INK.MUTED };

export const isCategoricalSlot = (slot: number): boolean => Number.isInteger(slot) && slot >= 0 && slot < CATEGORICAL_SLOT_COUNT;

export const categoricalColor = (slot: number): CategoricalColor => {
	if (!isCategoricalSlot(slot)) throw new Error(`Categorical slot ${slot} is out of range 0..${CATEGORICAL_SLOT_COUNT - 1}`);
	return { slot, dot: CATEGORICAL.DOT[slot], fill: CATEGORICAL.FILL[slot], ink: CATEGORICAL.INK[slot] };
};

const ALL_SLOTS = Array.from({ length: CATEGORICAL_SLOT_COUNT }, (_, slot) => slot);
const AUTO_SLOTS = ALL_SLOTS.slice(0, CATEGORICAL_AUTO_SLOTS);

/** The colour for the `index`-th item of an ordered list — cycles through the automatic slots only. */
export const categoricalColorAt = (index: number): CategoricalColor => categoricalColor(index % CATEGORICAL_AUTO_SLOTS);

/**
 * Colour for a free-form label that belongs to no list it could be ranked in (a person's avatar, a demo unit
 * name). Stable per label but two labels CAN share a colour — anything that has a list (jobs, roles,
 * departments) uses its position instead.
 */
export const freeLabelColor = (label: string): CategoricalColor => categoricalColorAt(Math.abs(hashString(label)));

/**
 * Slot per role. Ordered by how often roles share a schedule in a nursing facility, so the roles that
 * sit side by side get the most separated colours. Shared slots are the same job under two names
 * (LPN/LVN, CNA/CG/CA) or roles that live in different departments (the physician series MD/DO/ACN/PO).
 * `null` = no identity colour.
 */
const ROLE_TO_SLOT: Record<NurseFullRole, number | null> = {
	[NurseFullRole.RN]: 0,
	[NurseFullRole.CNA]: 1,
	[NurseFullRole.CG]: 1,
	[NurseFullRole.CA]: 1,
	[NurseFullRole.LPN]: 2,
	[NurseFullRole.LVN]: 2,
	[NurseFullRole.CMA]: 3,
	[NurseFullRole.MT]: 4,
	[NurseFullRole.NP]: 5,
	[NurseFullRole.RNA]: 6,
	[NurseFullRole.RLPN]: 6,
	[NurseFullRole.NAS]: 7,
	[NurseFullRole.MDS]: 8,
	[NurseFullRole.RT]: 9,
	[NurseFullRole.HA]: 10,
	[NurseFullRole.RA]: 11,
	[NurseFullRole.CK]: 8,
	[NurseFullRole.CKA]: 8,
	[NurseFullRole.DA]: 9,
	[NurseFullRole.RD]: 10,
	[NurseFullRole.LNA]: 10,
	[NurseFullRole.HK]: 11,
	[NurseFullRole.ACT]: 5,
	[NurseFullRole.REC]: 7,
	[NurseFullRole.UC]: 3,
	[NurseFullRole.LCSW]: 6,
	[NurseFullRole.tech]: 4,
	[NurseFullRole.CRNA]: 5,
	[NurseFullRole.MD]: 0,
	[NurseFullRole.DO]: 1,
	[NurseFullRole.ACN]: 3,
	[NurseFullRole.PO]: 4,
	[NurseFullRole.UDF]: null,
};

/** Short role code → selected palette slot; missing roles keep their automatic colour. */
export type RoleColorPins = Readonly<Record<string, number>>;
export type RoleColorResolver = (role: NurseFullRole) => CategoricalColor;
export type RoleGroupColorResolver = (roleGroup: string, roleGroups: Readonly<Record<string, readonly string[]>>) => CategoricalColor;

const roleSlot = (role: NurseFullRole, pins: RoleColorPins): number | null | undefined => {
	const code = NurseFullRoleShort[role];
	// DEFAULT: an unpinned role keeps its established automatic slot.
	return pins[code] ?? ROLE_TO_SLOT[role];
};

export const buildRoleColorResolver = (pins: RoleColorPins): RoleColorResolver => (role) => {
	const slot = roleSlot(role, pins);
	if (slot === null || slot === undefined) return NEUTRAL_CATEGORICAL_COLOR;
	return categoricalColor(slot);
};

export const roleColor = (role: NurseFullRole): CategoricalColor => {
	const slot = ROLE_TO_SLOT[role];
	// DEFAULT: `undefined` is a role string off the wire that isn't a known role (typed as one by a cast upstream);
	// it gets the no-identity colour rather than crashing the row that shows it.
	if (slot === null || slot === undefined) return NEUTRAL_CATEGORICAL_COLOR;
	return categoricalColor(slot);
};

// The cast only lets an arbitrary code index the enum; a code that isn't a role reads back `undefined`.
const shortRoleToFullRole = (shortRole: string): NurseFullRole | undefined => NurseFullRole[shortRole as keyof typeof NurseFullRole];

/** The one slot a group's roles all share, or `undefined` when they span several (or none is a known role). */
const singleRoleSlot = (members: readonly string[], pins: RoleColorPins): number | null | undefined => {
	const slots = new Set(
		members
			.map(shortRoleToFullRole)
			.filter((role) => role !== undefined)
			.map((role) => roleSlot(role, pins)),
	);
	return slots.size === 1 ? [...slots][0] : undefined;
};

/**
 * A role group's colour: a bare role code ("RN") is that role; a group whose roles are one role in all but name
 * (LPN + LVN) takes that role's colour; a group that mixes roles ("Nurse" = RN + LPN) gets a colour of its own,
 * taken in the department's group order from the slots no single-role group uses — so "Nurse" and "RN" never
 * look alike when both are sections.
 */
export const buildRoleGroupColorResolver = (pins: RoleColorPins): RoleGroupColorResolver => {
	const resolveRole = buildRoleColorResolver(pins);
	return (roleGroup, roleGroups) => {
		const directRole = shortRoleToFullRole(roleGroup);
		if (directRole) return resolveRole(directRole);
		const members = roleGroups[roleGroup];
		// DEFAULT: a group name the department no longer defines (stale settings, a removed group) has no
		// identity to show; neutral makes that visible instead of borrowing a live group's colour.
		if (members === undefined) return NEUTRAL_CATEGORICAL_COLOR;
		const ownSlot = singleRoleSlot(members, pins);
		if (ownSlot !== undefined) return ownSlot === null ? NEUTRAL_CATEGORICAL_COLOR : categoricalColor(ownSlot);
		// A group named after a role ("RN": ["RN"]) or made of one role holds that role's slot; the rest are mixed.
		const fixedSlotOf = (name: string): number | null | undefined => {
			const role = shortRoleToFullRole(name);
			return role ? roleSlot(role, pins) : singleRoleSlot(roleGroups[name], pins);
		};
		const groupNames = Object.keys(roleGroups);
		const singleSlots = new Set(groupNames.map(fixedSlotOf));
		const mixedGroups = groupNames.filter((name) => fixedSlotOf(name) === undefined);
		const free = ALL_SLOTS.filter((slot) => !singleSlots.has(slot));
		const cycle = free.length > 0 ? free : AUTO_SLOTS;
		return categoricalColor(cycle[mixedGroups.indexOf(roleGroup) % cycle.length]);
	};
};

/** Automatic role-group colours, retained for callers outside a department context. */
export const roleGroupColor = buildRoleGroupColorResolver({});

/** `jobId → slot` the user (or their department default) pinned. */
export type JobColorPins = Readonly<Record<string, number>>;

export type JobColorResolver = (jobId: string) => CategoricalColor;

/** Starting slots for common jobs; saved role choices do not change job colours. */
const JOB_ANCHOR_ROLE: Readonly<Record<string, NurseFullRole>> = {
	nurse: NurseFullRole.RN,
	aide: NurseFullRole.CNA,
};

const NO_RESERVED_SLOTS: ReadonlySet<number> = new Set();

const bySortOrder = (a: Job, b: Job): number => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);

/** Hands out `jobs` the given slots in order, wrapping through the automatic ones once they run out. */
const dealSlots = (jobs: readonly Job[], available: readonly number[], into: Map<string, number>): void => {
	// Every slot is taken: the rest wrap through the automatic ones — the label tells them apart.
	const cycle = available.length > 0 ? available : AUTO_SLOTS;
	for (const [index, job] of jobs.entries()) into.set(job.id, cycle[index % cycle.length]);
};

/**
 * The slot each job takes with no pins: Nurse/Aide their fixed starting slots, then the rest in job order — the automatic
 * slots first (the best-separated set), then the extras, before any colour repeats.
 */
const automaticSlots = (ranked: readonly Job[], reserved: ReadonlySet<number>): Map<string, number> => {
	const slots = new Map<string, number>();
	for (const job of ranked) {
		const anchor = JOB_ANCHOR_ROLE[job.name];
		const anchorSlot = anchor ? ROLE_TO_SLOT[anchor] : null;
		if (anchorSlot !== null && anchorSlot !== undefined) slots.set(job.id, anchorSlot);
	}
	const taken = new Set([...slots.values(), ...reserved]);
	dealSlots(
		ranked.filter((job) => !slots.has(job.id)),
		ALL_SLOTS.filter((slot) => !taken.has(slot)),
		slots,
	);
	return slots;
};

/**
 * Pins win; every other job keeps its automatic colour unless a pin took it, so pinning one job moves at most
 * the job that had that colour — never the rest of the group. A displaced job takes the next unused slot.
 */
const assignGroupSlots = (ranked: readonly Job[], pins: JobColorPins, reserved: ReadonlySet<number>): Map<string, number> => {
	const automatic = automaticSlots(ranked, reserved);
	const slots = new Map<string, number>();
	for (const job of ranked) {
		const pinned = pins[job.id];
		if (pinned !== undefined) slots.set(job.id, pinned);
	}
	const pinnedSlots = new Set(slots.values());
	const displaced: Job[] = [];
	for (const job of ranked) {
		if (slots.has(job.id)) continue;
		const slot = automatic.get(job.id);
		if (slot === undefined || pinnedSlots.has(slot)) displaced.push(job);
		else slots.set(job.id, slot);
	}
	const used = new Set([...slots.values(), ...reserved]);
	dealSlots(
		displaced,
		ALL_SLOTS.filter((slot) => !used.has(slot)),
		slots,
	);
	return slots;
};

/**
 * The department's enabled jobs share the palette among themselves; every other job (disabled, or from the
 * catalog on a picker that lists all jobs) is coloured afterwards from the slots they left free, so it can
 * never push an enabled job off its colour and only repeats one once the palette runs out.
 */
const assignJobSlots = (jobs: readonly Job[], enabledJobIds: readonly string[], pins: JobColorPins): Map<string, number> => {
	const enabled = new Set(enabledJobIds);
	const enabledSlots = assignGroupSlots(jobs.filter((job) => enabled.has(job.id)).sort(bySortOrder), pins, NO_RESERVED_SLOTS);
	const otherSlots = assignGroupSlots(jobs.filter((job) => !enabled.has(job.id)).sort(bySortOrder), pins, new Set(enabledSlots.values()));
	return new Map([...otherSlots, ...enabledSlots]);
};

/**
 * Colour per job, by position in the department's job list — never by name, so one job reads the same on
 * every screen and two jobs can only share a colour when there are more jobs than colours (or a user pinned
 * them together). Pinned slots win; Nurse/Aide start at the original RN/CNA palette slots independently of
 * saved role choices. Everything else takes the next free
 * slot — the eight automatic ones, then the four extras — enabled jobs first, in `sortOrder`.
 */
export const buildJobColorResolver = ({
	jobs,
	enabledJobIds,
	pins,
}: {
	jobs: readonly Job[];
	enabledJobIds: readonly string[];
	pins: JobColorPins;
}): JobColorResolver => {
	const slots = assignJobSlots(jobs, enabledJobIds, pins);
	return (jobId: string) => {
		const slot = slots.get(jobId);
		// DEFAULT: an id outside the department's jobs (archived, or disabled but still on old shifts) has no
		// slot here; neutral keeps it from taking a live job's colour.
		if (slot === undefined) return NEUTRAL_CATEGORICAL_COLOR;
		return categoricalColor(slot);
	};
};

/**
 * A shift's job colour, or `undefined` when it has no job or the job can't be resolved — so every view falls back
 * to the same role colour instead of one showing grey and another the role.
 */
export const shiftJobColor = (jobColor: JobColorResolver, jobId: string | undefined): CategoricalColor | undefined => {
	if (jobId === undefined) return undefined;
	const color = jobColor(jobId);
	return color.slot === null ? undefined : color;
};

/** Colour per department, by its position in the user's department list. */
export const departmentColor = (departmentId: string, orderedDepartmentIds: readonly string[]): CategoricalColor => {
	const index = orderedDepartmentIds.indexOf(departmentId);
	// DEFAULT: a department the user is no longer a member of (an old notification) has no place in their list.
	if (index < 0) return NEUTRAL_CATEGORICAL_COLOR;
	return categoricalColorAt(index);
};
